'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HeadingSubtitle, HeadingTitle } from '../shared/heading';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useState } from 'react';
import { Reveal } from '../ui/reveal';

export function ProductFeatures({ features }: { features: TDynamicData[] }) {
  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'w-full bg-primary pb-12 pt-10 sm:pb-20 sm:pt-16 lg:pb-24 lg:pt-20',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 lg:gap-10 xl:gap-12',
        )}
      >
        <div className="flex w-full flex-col-reverse items-start justify-start gap-2 md:flex-row md:items-start md:justify-between">
          <HeadingTitle title={t('text2320')} className="text-white" />
          <HeadingSubtitle subtitle={t('text2321')} type="white" />
        </div>
        <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-3">
          {features.map((feature, index) => (
            <ProductFeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductFeatureCard({ feature, index }: { feature: TDynamicData; index: number }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Reveal height='full'>
      <div
        className={cn(
          'group transition-all',
          'col-span-1 h-full w-full rounded-[8px] bg-black/20 p-6',
          'transition-all hover:bg-white',
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex h-full w-full items-stretch justify-between gap-12">
          <div className="flex flex-col items-start justify-between">
            <p
              className={cn(
                'text-xl font-medium tracking-tight text-white lg:text-2xl',
                'transition-all group-hover:text-primary',
              )}
            >
              0{index + 1}
            </p>
            <Image
              src={
                isHovered
                  ? feature.fields?.[1]?.images?.[1]?.url ||
                    feature.fields?.[1]?.images?.[0]?.url ||
                    '/images/placeholder.png'
                  : feature.fields?.[1]?.images?.[0]?.url || '/images/placeholder.png'
              }
              alt={resolveImageAlt((isHovered ? (feature?.fields?.[1]?.images?.[1]?.url ? feature?.fields?.[1]?.images?.[1] : feature?.fields?.[1]?.images?.[0]) : feature?.fields?.[1]?.images?.[0]), feature?.name)}
              width={0}
              height={0}
              priority
              loading="eager"
              className="max-h-10 min-h-10 min-w-10 max-w-10 object-contain object-center sm:max-h-12 sm:min-h-12 sm:min-w-12 sm:max-w-12 lg:max-h-16 lg:min-w-16 lg:max-w-16"
            />
          </div>
          <div className="flex w-full flex-col items-start justify-between gap-4 sm:gap-5 md:max-w-[248px] lg:gap-6">
            <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-3 lg:gap-4">
              <h3
                data-ea-visual-tag="h4"
                className={cn(
                  'text-xl font-medium tracking-tight text-white sm:text-2xl',
                  'transition-all group-hover:text-primary',
                )}
              >
                {feature.name}
              </h3>
              <div className="h-[1px] w-full max-w-[150px] bg-white/40" />
            </div>
            <p
              className={cn(
                'w-full text-sm font-light text-white lg:text-base',
                'transition-all group-hover:text-primary',
              )}
            >
              {feature.fields[0]?.value}
            </p>
          </div>
        </div>
      </div>
    </Reveal>
  );
}
